import { inject, Injectable } from "@angular/core";
import { firstValueFrom, Observable } from "rxjs";
import { DirectoryClientV2, DirectoryEntryQueryResult, DirectoryEntrySearchRequest, DirectoryEntryType, Governance } from "./directory-api.service";

/**
 * Thin wrapper over the generated {@link DirectoryClientV2} that collapses the directory's read/lookup
 * surface onto the single `directoryEntrySearchFull` route and resolves to plain promises so callers
 * don't have to unwrap observables. `governance` is a required argument (used for partition resolution),
 * and a missing filter value short-circuits rather than degrading into an unfiltered search. The
 * single-entry lookups ({@link getById} / {@link queryByOrg}) throw when no entry matches, restoring the
 * "value or error" contract of the point-read endpoints they replaced.
 */
@Injectable({ providedIn: "root" })
export class DirectoryClientService {
    private readonly client = inject(DirectoryClientV2);

    /**
     * Returns the full directory entry for a qualified directory OID (`id`). Throws when no entry is found
     * (or no id is supplied). Includes inactive entries by default to match the legacy point-read.
     */
    public async getById(id: string | undefined, governance: Governance, active = false): Promise<DirectoryEntryQueryResult> {
        const [entry] = id ? await this.search({ organizationId: id, governance }, active) : [];
        if (!entry) throw new Error(`Directory entry not found for id '${id ?? ""}'.`);
        return entry;
    }

    /** Returns the full directory entry for a Kno2 organization id. Throws when no entry is found. */
    public async queryByOrg(kno2OrganizationId: number | undefined, governance: Governance, active = false): Promise<DirectoryEntryQueryResult> {
        const [entry] = kno2OrganizationId != null ? await this.search({ kno2OrganizationId, governance }, active) : [];
        if (!entry) throw new Error(`Directory entry not found for Kno2 organization id '${kno2OrganizationId ?? ""}'.`);
        return entry;
    }

    /** Returns every entry whose hierarchy contains `parentId` (the directory subtree beneath an entry). */
    public getChildren(parentId: string | undefined, governance: Governance, active = false): Promise<DirectoryEntryQueryResult[]> {
        if (!parentId) return Promise.resolve([]);
        return this.search({ parentId, governance }, active);
    }

    /** Returns the top-level organizations for a governance (QHINs for TEFCA, Implementers for Carequality). */
    public getTopLevel(governance: Governance, directoryEntryType: DirectoryEntryType, active = true): Promise<DirectoryEntryQueryResult[]> {
        return firstValueFrom(this.search$({ directoryEntryType, governance }, active));
    }

    /**
     * Runs a directory search with caller-supplied criteria and returns the full entries (active-only by
     * default). Used directly by the DOA delegate search; the typed helpers above route through it too.
     */
    public search(request: DirectoryEntrySearchRequest, active = true): Promise<DirectoryEntryQueryResult[]> {
        return firstValueFrom(this.search$(request, active));
    }

    private search$(request: DirectoryEntrySearchRequest, active: boolean): Observable<DirectoryEntryQueryResult[]> {
        return this.client.directoryEntrySearchFull(active, request);
    }
}
